import React from 'react'
import { runIfFn } from '@mote/helpers/assertion'
import { __DEV__ } from '@mote/helpers/constants'
import type { PortalProps } from '../portal'
import { Portal } from '../portal'
import type { DialogCloseButtonProps } from './DialogCloseButton'
import { DialogCloseButton } from './DialogCloseButton'
import type { DialogContainerProps } from './DialogContainer'
import { DialogContainer } from './DialogContainer'
import { DialogContent } from './DialogContent'
import { DialogFooter } from './DialogFooter'
import { DialogHeader } from './DialogHeader'
import type { DialogOverlayProps } from './DialogOverlay'
import { DialogOverlay } from './DialogOverlay'
import { DialogProvider } from './DialogProvider'
import type { DialogOptions, ScrollBehavior, UseDialogProps } from './types'
import { useDialog } from './useDialog'

type Ref<T = any> = { ref?: React.RefObject<T> }

export interface DialogProps extends UseDialogProps, DialogOptions {
  children: React.ReactNode
  isCentered?: boolean
  showOverlay?: boolean
  showCloseButton?: boolean
  scrollBehavior?: ScrollBehavior
  portalProps?: Pick<PortalProps, 'appendToParentPortal' | 'containerRef'>
  rootProps?: DialogContainerProps & Ref<HTMLDivElement>
  overlayProps?: DialogOverlayProps & Ref<HTMLDivElement>
  closeButtonProps?: DialogCloseButtonProps & Ref<HTMLButtonElement>
  className?: string
}

/**
 * Heavily based on Chakra UI Modal component.
 * It provides context and functionality for all dialog components
 *
 * @see Docs     https://chakra-ui.com/docs/components/overlay/modal
 * @see Source   https://github.com/chakra-ui/chakra-ui/blob/main/packages/components/modal/src/modal.tsx
 * @see WAI-ARIA https://www.w3.org/TR/wai-aria-practices/#dialog_modal
 */
export const Dialog = (props: DialogProps) => {
  const {
    portalProps,
    children: _children,
    initialFocusRef,
    finalFocusRef,
    preserveScrollBarGap,
    focusGroup,
    showCloseButton = true,
    showOverlay = true,
    lockFocusAcrossFrames = true,
    returnFocusOnClose = true,
    trapFocus = true,
    autoFocus = true,
    blockScrollOnMount = true,
    allowPinchZoom = false,
    rootProps = {},
    overlayProps = {},
    closeButtonProps = {},
    className
  } = props
  const { ref: containerRef, ...restContainerProps } = rootProps
  const { ref: overlayRef, ...restOverlayProps } = overlayProps
  const { ref: closeButtonRef, ...restCloseButtonProps } = closeButtonProps

  const dialog = useDialog(props)

  const context = {
    ...dialog,
    autoFocus,
    trapFocus,
    focusGroup,
    initialFocusRef,
    finalFocusRef,
    returnFocusOnClose,
    blockScrollOnMount,
    allowPinchZoom,
    preserveScrollBarGap,
    lockFocusAcrossFrames
  }

  const children = runIfFn(_children, {
    isOpen: context.isOpen,
    onClose: context.onClose
  })

  return (
    <DialogProvider value={context}>
      {context.isMounted && (
        <Portal {...portalProps}>
          <DialogOverlay
            ref={overlayRef}
            {...restOverlayProps}
            showOverlay={showOverlay}
          />
          <DialogContainer
            ref={containerRef}
            className={className}
            {...restContainerProps}
          >
            {showCloseButton && (
              <DialogCloseButton
                ref={closeButtonRef}
                {...restCloseButtonProps}
              />
            )}
            {children}
          </DialogContainer>
        </Portal>
      )}
    </DialogProvider>
  )
}

if (__DEV__) {
  Dialog.displayName = 'Dialog'
}

Dialog.Overlay = DialogOverlay
Dialog.Container = DialogContainer
Dialog.CloseButton = DialogCloseButton
Dialog.Header = DialogHeader
Dialog.Content = DialogContent
Dialog.Footer = DialogFooter
